// src/services/chatApi.ts
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';

// Set up axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Add auth token to requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  search_results?: any[];
  feedbackScore?: number | null;
}

export interface Conversation {
  id: number;
  session_id: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message?: string;
}

export interface ChatRequest {
  message: string;
  conversation_id?: number;
}

export interface ChatResponse {
  message: string;
  conversation_id: number;
  message_id: number;
  search_results: any[];
  debug_info?: any;
}

export interface StreamCallbacks {
  onSources: (sources: any[]) => void;
  onToken: (token: string) => void;
  onDone: (data: { conversation_id: number; message_id: number }) => void;
  onError: (error: Error) => void;
  onRewriting?: (originalQuery: string) => void;
  onRewritten?: (rewrittenQuery: string) => void;
}

/**
 * Parse SSE events from a raw text buffer.
 *
 * SSE events are delimited by blank lines (`\n\n`). Each event consists of
 * one or more `data:` lines. This function consumes complete events from the
 * buffer and returns any leftover (incomplete) data so it can be prepended
 * to the next chunk from the stream.
 */
function parseSSEBuffer(
  buffer: string,
  callbacks: StreamCallbacks
): string {
  // Split on double-newline boundaries that separate SSE events.
  // We keep splitting as long as there are complete events in the buffer.
  const parts = buffer.split('\n\n');

  // The last element is either an incomplete event or an empty string
  // (if the buffer ended on a boundary). Keep it for the next iteration.
  const remainder = parts.pop() ?? '';

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    // Collect all `data:` lines within a single event block and join them.
    const dataLines: string[] = [];
    for (const line of trimmed.split('\n')) {
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (dataLines.length === 0) continue;

    const dataStr = dataLines.join('\n');

    try {
      const parsed = JSON.parse(dataStr);

      switch (parsed.type) {
        case 'rewriting':
          callbacks.onRewriting?.(parsed.original_query);
          break;
        case 'rewritten':
          callbacks.onRewritten?.(parsed.rewritten_query);
          break;
        case 'sources':
          callbacks.onSources(parsed.data);
          break;
        case 'token':
          callbacks.onToken(parsed.data);
          break;
        case 'done':
          callbacks.onDone({
            conversation_id: parsed.conversation_id,
            message_id: parsed.message_id,
          });
          break;
        default:
          // Silently ignore unknown event types for forward-compatibility.
          break;
      }
    } catch {
      // If a data line isn't valid JSON, skip it rather than crashing the stream.
      console.warn('SSE parse warning: non-JSON data line skipped:', dataStr);
    }
  }

  return remainder;
}

export const chatApi = {
  // Send a chat message (non-streaming, original endpoint)
  sendMessage: async (request: ChatRequest): Promise<ChatResponse> => {
    console.log('Sending chat message:', request);

    try {
      const response = await axios.post('/chat/message', request);
      console.log('Chat response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  },

  /**
   * Send a chat message and stream the response via SSE.
   *
   * Uses the native `fetch` API instead of axios because axios buffers
   * the entire response body — it cannot yield chunks as they arrive.
   *
   * Returns an AbortController so the caller can cancel the stream
   * (e.g. if the component unmounts before the response finishes).
   */
  sendMessageStream: (
    request: ChatRequest,
    callbacks: StreamCallbacks
  ): AbortController => {
    const controller = new AbortController();
    const token = localStorage.getItem('access_token');

    // Fire-and-forget the async work; errors are routed through callbacks.onError.
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/chat/message/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!response.ok) {
          // Handle 401 the same way the axios interceptor does.
          if (response.status === 401) {
            localStorage.removeItem('access_token');
            window.location.href = '/login';
            return;
          }

          const errorBody = await response.text().catch(() => '');
          throw new Error(
            `Stream request failed (${response.status}): ${errorBody || response.statusText}`
          );
        }

        if (!response.body) {
          throw new Error('Response body is null — streaming not supported by the browser.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          sseBuffer += decoder.decode(value, { stream: true });
          sseBuffer = parseSSEBuffer(sseBuffer, callbacks);
        }

        // Flush any trailing data that wasn't terminated by `\n\n`.
        if (sseBuffer.trim()) {
          parseSSEBuffer(sseBuffer + '\n\n', callbacks);
        }
      } catch (err: unknown) {
        // AbortError is expected when the caller cancels; don't propagate it.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        callbacks.onError(err instanceof Error ? err : new Error(String(err)));
      }
    })();

    return controller;
  },

  // Get conversation history
  getConversations: async (): Promise<Conversation[]> => {
    try {
      const response = await axios.get('/chat/conversations');
      return response.data;
    } catch (error) {
      console.error('Error getting conversations:', error);
      throw error;
    }
  },

  // Get messages for a conversation
  getConversationMessages: async (conversationId: number): Promise<{ messages: ChatMessage[] }> => {
    try {
      const response = await axios.get(`/conversations/${conversationId}/messages`);
      return response.data;
    } catch (error) {
      console.error('Error getting conversation messages:', error);
      throw error;
    }
  },

  // Delete conversation
  deleteConversation: async (conversationId: number): Promise<void> => {
    try {
      await axios.delete(`/conversations/${conversationId}`);
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  },

  // Submit feedback on a message
  submitFeedback: async (messageId: number, score: number): Promise<void> => {
    await axios.post(`/chat/messages/${messageId}/feedback`, { score });
  },
};
